import React, { useState, useEffect, useCallback } from 'react';
import { OwnerLayout } from '../../components/Layout';
import { useAuth } from '../../context/AuthContext';
import { Textarea } from '../../components/ui/textarea';
import { 
  Flame, BookOpen, Calendar, Trophy, Target, PenLine,
  Trash2, ChevronDown, ChevronUp, Sparkles
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Streak fire color based on streak length
const getStreakColor = (streak) => {
  if (streak >= 30) return 'text-red-500';
  if (streak >= 14) return 'text-orange-500';
  if (streak >= 7) return 'text-amber-500';
  if (streak >= 3) return 'text-yellow-500';
  return 'text-gray-400';
};

// Mini calendar component (last 35 days)
function StreakCalendar({ calendarData }) {
  const days = [];
  const today = new Date();
  for (let i = 34; i >= 0; i--) {
    const d = new Date(today);
    d.setDate(d.getDate() - i);
    const dateStr = d.toISOString().split('T')[0];
    const count = calendarData[dateStr] || 0;
    days.push({ date: dateStr, count, day: d });
  }

  const dayLabels = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

  return (
    <div>
      <div className="grid grid-cols-7 gap-1 mb-1">
        {dayLabels.map((l, i) => (
          <div key={i} className="text-[10px] text-gray-400 text-center font-body">{l}</div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {days.map((d) => (
          <div
            key={d.date}
            data-testid={`cal-${d.date}`}
            title={`${d.date}: ${d.count} ${d.count === 1 ? 'entry' : 'entries'}`}
            className={`w-full aspect-square rounded-md transition-colors ${
              d.count >= 2 ? 'bg-edupaws-sage' :
              d.count === 1 ? 'bg-edupaws-sage/50' :
              'bg-gray-100'
            }`}
          />
        ))}
      </div>
      <div className="flex items-center gap-2 mt-2 justify-end">
        <span className="text-[10px] text-gray-400 font-body">Less</span>
        <div className="w-3 h-3 rounded-sm bg-gray-100" />
        <div className="w-3 h-3 rounded-sm bg-edupaws-sage/50" />
        <div className="w-3 h-3 rounded-sm bg-edupaws-sage" />
        <span className="text-[10px] text-gray-400 font-body">More</span>
      </div>
    </div>
  );
}

export default function Journal() {
  const { user, activeDog } = useAuth();
  const [entries, setEntries] = useState([]);
  const [streak, setStreak] = useState(null);
  const [calendarData, setCalendarData] = useState({});
  const [content, setContent] = useState('');
  const [selectedTags, setSelectedTags] = useState([]);
  const [tags, setTags] = useState([]);
  const [milestones, setMilestones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showAllEntries, setShowAllEntries] = useState(false);

  const fetchData = useCallback(async () => {
    if (!activeDog) { setLoading(false); return; }
    try {
      const [entriesRes, streakRes, calRes, tagsRes, milestonesRes] = await Promise.all([
        axios.get(`${API}/journal/${activeDog.id}`),
        axios.get(`${API}/journal/${activeDog.id}/streak`),
        axios.get(`${API}/journal/${activeDog.id}/calendar`),
        axios.get(`${API}/journal/tags`),
        axios.get(`${API}/journal/milestones`)
      ]);
      setEntries(entriesRes.data || []);
      setStreak(streakRes.data);
      setCalendarData(calRes.data || {});
      setTags(tagsRes.data || []);
      setMilestones(milestonesRes.data || []);
    } catch (e) {
      console.error('Error fetching journal data:', e);
    } finally {
      setLoading(false);
    }
  }, [activeDog]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSubmit = async () => {
    if (!content.trim()) { toast.error('Write something about your training session!'); return; }
    if (!activeDog) return;
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/journal`, {
        dog_id: activeDog.id,
        owner_user_id: user.id,
        content: content.trim(),
        tags: selectedTags
      });
      setContent('');
      setSelectedTags([]);
      if (res.data.new_milestone) {
        toast.success(`${res.data.new_milestone.title}! ${res.data.new_milestone.message}`, { duration: 6000 });
      } else {
        toast.success('Journal entry saved!');
      }
      fetchData();
    } catch {
      toast.error('Failed to save entry');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (entryId) => {
    try {
      await axios.delete(`${API}/journal/${entryId}`);
      toast.success('Entry deleted');
      fetchData();
    } catch { toast.error('Failed to delete'); }
  };

  const toggleTag = (tag) => {
    setSelectedTags(prev => prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]);
  };

  if (loading) {
    return (
      <OwnerLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-edupaws-sage border-t-transparent"></div>
        </div>
      </OwnerLayout>
    );
  }

  if (!activeDog) {
    return (
      <OwnerLayout>
        <div className="text-center py-16" data-testid="journal-no-dog">
          <BookOpen className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h2 className="font-heading text-2xl font-bold text-gray-800 mb-2">Add a Dog First</h2>
          <p className="text-gray-500 font-body">You need a dog to start journaling your training sessions.</p>
        </div>
      </OwnerLayout>
    );
  }

  const journaledToday = streak?.journaled_today;
  const currentStreak = streak?.current_streak || 0;
  const longestStreak = streak?.longest_streak || 0;
  const totalEntries = streak?.total_entries || 0;
  const nextMilestone = streak?.next_milestone;
  const currentMilestone = streak?.current_milestone;
  const displayedEntries = showAllEntries ? entries : entries.slice(0, 5);

  return (
    <OwnerLayout>
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="font-heading text-3xl md:text-4xl font-bold text-gray-800 flex items-center gap-3">
            <BookOpen className="w-8 h-8 text-edupaws-sage" />
            Training Journal
          </h1>
          <p className="text-gray-500 font-body mt-1">
            Track your daily training with {activeDog.name} and build your streak
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Left: Entry Form + History */}
          <div className="lg:col-span-2 space-y-6">
            {/* Streak Banner */}
            {!journaledToday && currentStreak > 0 && (
              <div className="bg-amber-50 border-2 border-amber-200 rounded-2xl p-4 flex items-center gap-4" data-testid="streak-warning">
                <Flame className="w-8 h-8 text-amber-500 flex-shrink-0" />
                <div>
                  <p className="font-heading font-bold text-amber-800">
                    Don&apos;t break your {currentStreak}-day streak!
                  </p>
                  <p className="text-sm text-amber-600 font-body">
                    Log today&apos;s training to keep your streak alive.
                  </p>
                </div>
              </div>
            )}

            {journaledToday && (
              <div className="bg-green-50 border-2 border-green-200 rounded-2xl p-4 flex items-center gap-4" data-testid="journaled-today">
                <Sparkles className="w-8 h-8 text-green-500 flex-shrink-0" />
                <div>
                  <p className="font-heading font-bold text-green-800">
                    You trained today! Keep it up!
                  </p>
                  <p className="text-sm text-green-600 font-body">
                    Add more entries or review your progress below.
                  </p>
                </div>
              </div>
            )}

            {/* New Entry Form */}
            <div className="bg-white rounded-3xl border-2 border-gray-100 shadow-lg p-6" data-testid="journal-form">
              <h2 className="font-heading text-xl font-bold text-gray-800 flex items-center gap-2 mb-4">
                <PenLine className="w-5 h-5 text-edupaws-sage" />
                Log Training Session
              </h2>

              {/* Quick Tags */}
              <div className="mb-4">
                <label className="text-sm font-body font-bold text-gray-600 block mb-2">What did you work on?</label>
                <div className="flex flex-wrap gap-2">
                  {tags.map((tag) => (
                    <button
                      key={tag}
                      data-testid={`tag-${tag.toLowerCase().replace(' ', '-')}`}
                      onClick={() => toggleTag(tag)}
                      className={`px-3 py-1.5 rounded-full text-sm font-body font-bold transition-all ${
                        selectedTags.includes(tag)
                          ? 'bg-edupaws-sage text-white shadow-sm'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      {tag}
                    </button>
                  ))}
                </div>
              </div>

              {/* Content */}
              <Textarea
                data-testid="journal-content"
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder={`What did you and ${activeDog.name} work on today? How did it go?`}
                className="min-h-[120px] rounded-xl mb-4 resize-none"
              />

              <button
                data-testid="save-journal-btn"
                onClick={handleSubmit}
                disabled={submitting || !content.trim()}
                className="w-full bg-edupaws-navy text-white py-3 rounded-full font-heading font-bold text-lg
                  shadow-[0_4px_0_#2D3B47] hover:shadow-[0_2px_0_#2D3B47] hover:translate-y-[2px]
                  disabled:opacity-40 disabled:cursor-not-allowed transition-all"
              >
                {submitting ? 'Saving...' : 'Save Entry'}
              </button>
            </div>

            {/* Journal History */}
            <div>
              <h2 className="font-heading text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                <Calendar className="w-5 h-5 text-edupaws-sage" />
                Past Entries
              </h2>

              {entries.length === 0 ? (
                <div className="bg-white rounded-2xl border border-gray-100 p-8 text-center">
                  <BookOpen className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500 font-body">No journal entries yet. Start your first one above!</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {displayedEntries.map((entry) => (
                    <div
                      key={entry.id}
                      data-testid={`journal-entry-${entry.id}`}
                      className="bg-white rounded-2xl border border-gray-100 p-4 group hover:border-edupaws-sage/30 transition-colors"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          {entry.tags && entry.tags.length > 0 && (
                            <div className="flex flex-wrap gap-1.5 mb-2">
                              {entry.tags.map((tag) => (
                                <span key={tag} className="px-2 py-0.5 bg-edupaws-sage/10 text-edupaws-sage text-xs rounded-full font-body font-bold">
                                  {tag}
                                </span>
                              ))}
                            </div>
                          )}
                          <p className="text-gray-700 font-body text-sm whitespace-pre-wrap">{entry.content}</p>
                          <div className="text-xs text-gray-400 font-body mt-2">
                            {new Date(entry.created_at).toLocaleDateString('en-US', {
                              weekday: 'short', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
                            })}
                          </div>
                        </div>
                        <button
                          onClick={() => handleDelete(entry.id)}
                          className="text-gray-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
                          title="Delete entry"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}

                  {entries.length > 5 && (
                    <button
                      onClick={() => setShowAllEntries(!showAllEntries)}
                      className="w-full py-3 text-center text-sm font-body font-bold text-edupaws-sage hover:text-edupaws-navy transition-colors flex items-center justify-center gap-1"
                    >
                      {showAllEntries ? (
                        <><ChevronUp className="w-4 h-4" /> Show Less</>
                      ) : (
                        <><ChevronDown className="w-4 h-4" /> Show All {entries.length} Entries</>
                      )}
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Right Sidebar: Streak Stats */}
          <div className="space-y-6">
            {/* Streak Card */}
            <div className="bg-white rounded-3xl border-2 border-gray-100 shadow-lg p-6 text-center" data-testid="streak-card">
              <Flame className={`w-14 h-14 mx-auto mb-2 ${getStreakColor(currentStreak)}`} />
              <div className="font-heading text-5xl font-bold text-gray-800" data-testid="streak-count">
                {currentStreak}
              </div>
              <div className="text-gray-500 font-body text-sm mb-4">Day Streak</div>

              {currentMilestone && (
                <div className="bg-amber-50 rounded-xl px-4 py-3 mb-4" data-testid="current-milestone">
                  <div className="font-heading font-bold text-amber-800 text-sm">{currentMilestone.title}</div>
                  <div className="text-xs text-amber-600 font-body">{currentMilestone.message}</div>
                </div>
              )}

              {nextMilestone && (
                <div className="text-sm font-body text-gray-500">
                  <Target className="w-4 h-4 inline-block mr-1 text-edupaws-sage" />
                  Next: <strong className="text-gray-700">{nextMilestone.title}</strong> in {nextMilestone.days - currentStreak} days
                </div>
              )}
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-white rounded-2xl border border-gray-100 p-4 text-center">
                <div className="font-heading text-2xl font-bold text-gray-800">{longestStreak}</div>
                <div className="text-xs text-gray-500 font-body">Best Streak</div>
              </div>
              <div className="bg-white rounded-2xl border border-gray-100 p-4 text-center">
                <div className="font-heading text-2xl font-bold text-gray-800">{totalEntries}</div>
                <div className="text-xs text-gray-500 font-body">Total Entries</div>
              </div>
            </div>

            {/* Calendar Heatmap */}
            <div className="bg-white rounded-3xl border-2 border-gray-100 shadow-lg p-6" data-testid="streak-calendar">
              <h3 className="font-heading font-bold text-gray-800 text-sm mb-3 flex items-center gap-2">
                <Calendar className="w-4 h-4 text-edupaws-sage" />
                Training Activity
              </h3>
              <StreakCalendar calendarData={calendarData} />
            </div>

            {/* Milestones Progress */}
            <div className="bg-white rounded-3xl border-2 border-gray-100 shadow-lg p-6" data-testid="milestones-list">
              <h3 className="font-heading font-bold text-gray-800 text-sm mb-3 flex items-center gap-2">
                <Trophy className="w-4 h-4 text-amber-500" />
                Streak Milestones
              </h3>
              <div className="space-y-2">
                {milestones.slice(0, 6).map((m) => {
                  const achieved = currentStreak >= m.days;
                  return (
                    <div
                      key={m.days}
                      className={`flex items-center gap-3 p-2 rounded-lg transition-colors ${
                        achieved ? 'bg-amber-50' : 'opacity-50'
                      }`}
                    >
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                        achieved ? 'bg-amber-500 text-white' : 'bg-gray-200 text-gray-400'
                      }`}>
                        {achieved ? '✓' : m.days}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className={`font-body font-bold text-xs ${achieved ? 'text-amber-800' : 'text-gray-500'}`}>
                          {m.title}
                        </div>
                      </div>
                      <span className="text-[10px] text-gray-400 font-body">{m.days}d</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </OwnerLayout>
  );
}
